import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Check, Lock, ShieldCheck, MoonStar, ChevronDown, Sparkles, ArrowLeft, X, Star, Quote, Flame, CreditCard } from "lucide-react";
import { api } from "../../lib/api";
import { ensureFonts, FONT_AR, FONT_DISPLAY, visitorId } from "../../lib/sevenTheme";
import InAppBrowserHint from "../../components/InAppBrowserHint";
import { BUNDLES, ACCESS } from "../../data/sevenNights";
import SevenChatbot from "../../components/SevenChatbot";

const track = (event, props = {}) => {
  try {
    const payload = JSON.stringify({ event, props, visitor_id: visitorId(), page: "landing" });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/seven/track`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    else api.post("/seven/track", JSON.parse(payload));
  } catch (_) { /* analytics never breaks the funnel */ }
};

// TikTok pixel event helper — wraps ttq.track with bundle-specific content payload.
// Each browser call shares an event_id with the corresponding S2S event (when present)
// so TikTok dedupes correctly. AED price sent as nominal USD (×0.27 — AED pegged
// ~3.67 to USD) so TikTok's USD-only benchmark aggregates with the rest of the
// multi-product reporting.
const ttFire = (eventName, bundleKey, opts = {}) => {
  if (typeof window === "undefined" || !window.ttq?.track) return;
  const b = BUNDLES.find((x) => x.key === bundleKey) || BUNDLES[2];
  const usdValue = Math.round(b.price * 0.27 * 100) / 100;
  const payload = {
    contents: [{
      content_id: `7nights_${b.key}`,
      content_type: "product",
      content_name: `7 ليالي بيننا — ${b.label}`,
      quantity: 1,
      price: usdValue,
    }],
    value: usdValue,
    currency: "USD",
  };
  try { window.ttq.track(eventName, payload, opts.eventId ? { event_id: opts.eventId } : undefined); }
  catch (_) { /* analytics never breaks the funnel */ }
};

const Logo = ({ className = "" }) => (
  <img src="/images/7nights-logo.png" alt="7 ليالي بيننا" className={className} />
);

const TrustChip = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[#CFC3A1] bg-[#1C1010]/60 border border-[#C9A66A]/20 rounded-full px-3 py-1.5">
    <Check className="w-3 h-3 text-[#C9A66A]" strokeWidth={3} />
    {children}
  </span>
);

const SectionTitle = ({ kicker, children }) => (
  <div className="text-center mb-10">
    {kicker && (
      <div className="text-[10.5px] tracking-[0.32em] text-[#C9A66A] font-bold mb-3">{kicker}</div>
    )}
    <h2 className="font-bold text-[26px] sm:text-[34px] lg:text-[40px] leading-[1.3] text-white max-w-2xl mx-auto" style={{ fontFamily: FONT_DISPLAY, fontWeight: 800 }}>
      {children}
    </h2>
  </div>
);

const FAQ_DATA = [
  { q: "هل المحتوى فاضح؟", a: "لا. المحتوى مصمم للأزواج فقط لكنه مكتوب بذوق وخصوصية واحترام." },
  { q: "هل هو مناسب للخليج؟", a: "نعم. اللغة، الأمثلة، والأسلوب كلها مصممة للزوجين الخليجيين." },
  { q: "هل أحتاج أطبع شيء؟", a: "لا. كل شيء يعمل من الجوال مباشرة." },
  { q: "هل يصلني شيء على البيت أو على البريد؟", a: "لا. التجربة كاملة تفتح على الموقع فور الدفع — بدون بريد إلكتروني وبدون شحن." },
  { q: "هل يصلح للعرسان فقط؟", a: "لا. فيه نسخة للعرسان ونسخة للأزواج بعد الروتين." },
  { q: "هل هذا علاج زوجي؟", a: "لا. هو تجربة تقارب وترتيب ليالي خاصة، وليس بديلًا عن المختصين في الحالات الصعبة." },
  { q: "هل في ضمان استرداد؟", a: "نعم. ضمان استرداد كامل خلال 7 أيام إذا ما عجبكِ المحتوى. بدون أسئلة محرجة." },
];

// Anonymous Gulf wives testimonials — premium tone, feminine, varied profiles
// (newlywed, mom of 3, post-routine). Names are first-name only / "أم …" style
// to feel real without disclosing identity. UAE-pivot (Feb 2026): locations
// updated to UAE cities to mirror the new geo-targeting.
const TESTIMONIALS = [
  {
    name: "أم محمد",
    location: "دبي",
    bundle: "النسخة الكاملة",
    rating: 5,
    text: "كنت أحس إن بيننا حب بس اليوم ما يشبه أمس. جربت أول ليلتين وضحكنا أكثر من شهر كامل. أحلى ٢٠٠ درهم صرفتها على علاقتنا.",
  },
  {
    name: "نورة",
    location: "أبوظبي",
    bundle: "نسخة الرجوع",
    rating: 5,
    text: "زوجي ما يحب يفتح مواضيع، لكن الأسئلة المكتوبة سهّلت علينا الكلام. صرت أحس إنه قريب مني بدون ما أضغط عليه.",
  },
  {
    name: "أم تركي",
    location: "الشارقة",
    bundle: "النسخة الكاملة",
    rating: 5,
    text: "بعد ٧ سنين زواج وثلاث أطفال، حسيت إن الروتين بلعنا. هالبرنامج رجعنا ندردش ونضحك مثل الخطوبة. شي بسيط بس فرق.",
  },
  {
    name: "ريم",
    location: "العين",
    bundle: "نسخة البداية",
    rating: 5,
    text: "متزوجة جديد ومحتارة كيف نبدأ بدون إحراج. البرنامج فتح لنا باب لطيف للتعارف العميق. زوجي طلب نكمل بقية الليالي.",
  },
];

export default function LandingSeven() {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState(null);

  useEffect(() => {
    ensureFonts();
    document.title = "7 ليالي بيننا — تجربة رقمية للأزواج";
    track("page_view");
    // TikTok pixel: PageView is fired globally by index.html on every route.
    // We add a 7nights-specific ViewContent so the pixel sees product context
    // for retargeting + lookalike audiences.
    ttFire("ViewContent", "complete");
    const t0 = Date.now();
    const onUnload = () => track("time_on_site", { seconds: Math.round((Date.now() - t0) / 1000) });
    window.addEventListener("beforeunload", onUnload);
    return () => { window.removeEventListener("beforeunload", onUnload); };
  }, []);

  const goCheckout = (bundleKey, where) => {
    track("cta_click", { where, bundle: bundleKey });
    // TikTok AddToCart — every CTA click on the landing counts as cart intent.
    ttFire("AddToCart", bundleKey);
    // Google Ads — Add To Cart conversion. Shared tag across funnels.
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18239642780/L2brCPLJhL8cEJy5q_lD",
          value: 1.0,
          currency: "EGP",
        });
      }
    } catch (_) { /* analytics never breaks the funnel */ }
    navigate(`/7days/checkout?bundle=${bundleKey}`);
  };

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-[#0A0606] text-[#F5EAD3] selection:bg-[#C9A66A]/40"
      style={{ fontFamily: FONT_AR }}
      data-testid="seven-landing-root"
    >
      <InAppBrowserHint />
      {/* Scarcity / urgency banner — sticky at the very top. Static copy
          per product owner (no live counter). */}
      <div
        className="relative z-40 text-center px-4 py-2 text-[12px] sm:text-[12.5px] font-bold text-[#0A0606] flex items-center justify-center gap-2"
        style={{ background: "linear-gradient(90deg,#E2C281 0%,#D6B477 50%,#C9A66A 100%)" }}
        data-testid="seven-scarcity-banner"
      >
        <Flame className="w-3.5 h-3.5" />
        <span>سعر الإطلاق — وفّري حتى ١٠٠ درهم على النسخة الكاملة (٢٩٩ ← ١٩٩)</span>
      </div>
      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-[#0A0606]/85 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          <Link to="/7days" className="flex items-center gap-2" data-testid="seven-logo">
            <Logo className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="font-extrabold text-[15px] text-white tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>7 ليالي بيننا</div>
              <div className="text-[10px] text-[#A7956E] tracking-[0.16em]">تجربة خاصة للأزواج</div>
            </div>
          </Link>
          <button
            onClick={() => goCheckout("complete", "nav")}
            data-testid="seven-nav-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606] font-extrabold text-[12.5px] tracking-wide rounded-full px-5 py-2.5 transition-all shadow-[0_8px_28px_-10px_rgba(201,166,106,0.6)]"
          >
            ابدئي — <span className="line-through text-[#5A4A28] text-[11px] mr-1">299</span> 199 درهم <ArrowLeft className="w-3.5 h-3.5" />
          </button>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative overflow-hidden border-b border-white/[0.04]">
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(201,166,106,0.18) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-5xl mx-auto px-5 py-16 sm:py-24 lg:py-32 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] text-[#C9A66A] font-bold mb-6 bg-[#1C1010]/60 border border-[#C9A66A]/25 rounded-full px-4 py-1.5">
            <MoonStar className="w-3.5 h-3.5" />
            <span>للزوجات فقط — خصوصية كاملة</span>
          </div>
          <h1
            className="font-bold text-[32px] sm:text-[46px] lg:text-[58px] leading-[1.25] text-white max-w-3xl mx-auto"
            style={{ fontFamily: FONT_DISPLAY, fontWeight: 800 }}
            data-testid="seven-hero-title"
          >
            تشتاقين له <span className="text-[#C9A66A]">وهو جنبكِ؟</span>
          </h1>
          <p className="mt-6 text-[15.5px] sm:text-[17px] text-[#CFC3A1] max-w-2xl mx-auto leading-[1.95]">
            <span className="text-white font-semibold">«7 ليالي بيننا»</span> تجربة رقمية خاصة تساعدكِ ترجعين الكلام، الضحك، الدلع، والقرب مع زوجكِ — بدون إحراج، وبأسلوب راقٍ يناسب الزوجات في الإمارات والخليج.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => goCheckout("complete", "hero")}
              data-testid="seven-hero-cta"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606] font-extrabold text-[15px] tracking-wide rounded-full px-8 py-4 min-h-[58px] transition-all shadow-[0_14px_44px_-8px_rgba(201,166,106,0.55)]"
            >
              ابدئي أول ليلة <ArrowLeft className="w-4 h-4" />
            </button>
            <a
              href="#inside"
              data-testid="seven-hero-secondary"
              className="inline-flex items-center justify-center gap-1.5 text-[13.5px] text-[#CFC3A1] hover:text-[#C9A66A] underline underline-offset-[6px] decoration-[#C9A66A]/40 hover:decoration-[#C9A66A] transition-colors px-4 py-3"
            >
              شاهدي ما بداخل البرنامج
            </a>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
            <TrustChip>وصول فوري على الموقع</TrustChip>
            <TrustChip>بدون بريد إلكتروني</TrustChip>
            <TrustChip>خصوصية كاملة</TrustChip>
            <TrustChip>للأزواج فقط</TrustChip>
            <TrustChip>مناسب للخليج</TrustChip>
          </div>
        </div>
      </header>

      {/* HERO IMAGE — cinematic Gulf couple scene. Sits directly after the
          hero block (headline + sub + CTA + chips) and before the audience
          section, so the visitor lands on copy → emotion → self-identify.
          Image is rendered fully (no overlays) so the in-image headline text
          stays visible; the gold frame keeps it premium. */}
      <section className="border-b border-white/[0.04]" aria-hidden="true" data-testid="seven-hero-image">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
          <div className="rounded-[20px] overflow-hidden border border-[#C9A66A]/40 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.65)]">
            <img
              src="/images/7nights-hero-scene.png"
              alt=""
              loading="lazy"
              decoding="async"
              className="w-full h-auto block"
            />
          </div>
        </div>
      </section>

      {/* AUDIENCE — moved up so visitor sees herself in the first scroll. */}
      <section className="py-16 sm:py-20 border-b border-white/[0.04]">
        <div className="max-w-4xl mx-auto px-5">
          <SectionTitle kicker="هذا البرنامج لكِ إذا">من هذا البرنامج لكِ؟</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
            {[
              "أنتِ عروس جديدة وتبغين بداية قريبة بدون إحراج",
              "دخلتِ في الروتين وحسّيتِ إن الكلام صار مسؤوليات",
              "صرتِ أم… والوقت اللي لكما اختفى",
              "تحبين زوجكِ بس ما تعرفين كيف ترجعين اللحظة",
              "تبين تجربة خاصة بينكما — بذوق وخصوصية",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#1C1010]/40 border border-[#C9A66A]/15 rounded-xl px-4 py-3.5 text-[14px] text-[#E7DDC0]">
                <Heart className="w-4 h-4 text-[#C9A66A] mt-0.5 shrink-0" strokeWidth={2} />
                <span>{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[12.5px] text-[#A7956E] leading-relaxed max-w-xl mx-auto">
            هذا المنتج ليس علاجًا نفسيًا أو استشارة زوجية. هو تجربة رقمية للتقارب، الحوار، والاهتمام بين الزوجين.
          </p>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle kicker="المشكلة">
            العلاقة لا تنتهي فجأة… أحيانًا فقط تغطيها الأيام.
          </SectionTitle>
          <p className="text-[15px] text-[#CFC3A1] leading-[2] text-center max-w-2xl mx-auto">
            الدوام، البيت، الأطفال، الجوال، التعب، والروتين يجعلون العلاقة تتحول من حب إلى إدارة يومية. لا يوجد خلاف كبير، ولا مشكلة واضحة، لكن هناك شيء ناقص: <span className="text-white">لحظة حقيقية بينكم.</span>
          </p>
          <div className="mt-9 space-y-2.5 max-w-xl mx-auto">
            {[
              "تجلسون مع بعض لكن كل شخص في جواله",
              "الكلام صار طلبات ومسؤوليات",
              "الضحك قل",
              "الدلع صار نادر",
              "القرب موجود في القلب لكنه لا يظهر في اليوم",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 text-[14.5px] text-[#E7DDC0] bg-[#1C1010]/40 border border-[#C9A66A]/12 rounded-lg px-4 py-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A66A] mt-2.5 shrink-0" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION + HOW IT WORKS */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle kicker="الحل">
            سبع ليالٍ مكتوبة لكِ خطوة بخطوة.
          </SectionTitle>
          <p className="text-[15px] text-[#CFC3A1] leading-[2] text-center">
            بدلًا من أن تحاولي تبدئين الكلام من الصفر، البرنامج يعطيكِ سيناريو واضح لكل ليلة: كيف تبدئين، ماذا تقولين، ما النشاط، ما السؤال، وما اللمسة الأخيرة التي تخلي الليلة مختلفة.
          </p>
          <div className="mt-12 grid sm:grid-cols-2 gap-3">
            {[
              "اختاري النسخة المناسبة لكِ",
              "افتحي البرنامج من الجوال بعد الدفع",
              "ابدئي أول ليلة",
              "اتبعي الخطوات البسيطة",
              "كرري التجربة لمدة 7 ليالٍ",
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-4 bg-[#1C1010]/50 border border-[#C9A66A]/15 rounded-xl px-5 py-4">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center font-extrabold text-[#0A0606] text-[14px] shrink-0">{i + 1}</span>
                <span className="text-[14px] text-[#E7DDC0]">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT NIGHTS */}
      <section id="inside" className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-5">
          <SectionTitle kicker="بداخل التجربة">
            ليس مجرد أسئلة. تجربة كاملة.
          </SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { n: 1, t: "ليلة نرجع نضحك" },
              { n: 2, t: "ليلة اللي بخاطري" },
              { n: 3, t: "ليلة دلع بدون تكلف" },
              { n: 4, t: "ليلة نعرف بعض من جديد" },
              { n: 5, t: "ليلة بدون أدوار" },
              { n: 6, t: "ليلة جرأة بذوق" },
              { n: 7, t: "ليلة اتفاق جديد" },
            ].map((c) => (
              <div key={c.n} className="group relative bg-gradient-to-b from-[#1C1010] to-[#150C0C] border border-[#C9A66A]/20 hover:border-[#C9A66A]/55 rounded-2xl p-6 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] tracking-[0.26em] text-[#A7956E] font-bold">ليلة</span>
                  <span className="text-[28px] font-extrabold text-[#C9A66A] tabular-nums leading-none" style={{ fontFamily: FONT_DISPLAY }}>{c.n}</span>
                </div>
                <div className="text-[16px] font-bold text-white leading-snug">{c.t}</div>
              </div>
            ))}
            <div className="relative bg-gradient-to-b from-[#2A1414] to-[#150C0C] border border-[#C9A66A]/40 rounded-2xl p-6">
              <Sparkles className="w-5 h-5 text-[#C9A66A] mb-3" />
              <div className="text-[14px] text-white font-bold leading-relaxed">
                + بطاقات أسئلة، تحديات لطيفة، رسائل واتساب جاهزة، وأفكار ليالي بيتية
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXAMPLE NIGHT — full premium preview of one real night from the program */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.20] pointer-events-none"
          style={{ background: "radial-gradient(ellipse 50% 60% at 50% 50%, rgba(201,166,106,0.25) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-5">
          <SectionTitle kicker="نموذج من البرنامج">شوفوا ليلة كاملة قبل أن تشتروا.</SectionTitle>
          <div className="bg-gradient-to-b from-[#1C1010] to-[#0F0808] border border-[#C9A66A]/35 rounded-2xl shadow-[0_30px_90px_-25px_rgba(201,166,106,0.35)] overflow-hidden" data-testid="seven-example-night">
            <div className="bg-gradient-to-l from-[#2A1414] via-[#1F0E0E] to-[#150C0C] border-b border-[#C9A66A]/25 p-6 sm:p-7 flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] tracking-[0.28em] text-[#C9A66A] font-bold mb-2">ليلة من برنامج «بعد الروتين»</div>
                <h3 className="font-extrabold text-[22px] sm:text-[28px] text-white leading-tight" style={{ fontFamily: FONT_DISPLAY }}>
                  ليلة <span className="text-[#C9A66A]">نرجع نضحك</span>
                </h3>
                <div className="mt-2 text-[12.5px] text-[#A7956E] flex items-center gap-1.5">
                  <MoonStar className="w-3.5 h-3.5 text-[#C9A66A]" /> 30 إلى 60 دقيقة · الجوال بعيد
                </div>
              </div>
              <div className="text-left shrink-0">
                <div className="text-[44px] sm:text-[56px] font-black text-[#C9A66A] tabular-nums leading-none" style={{ fontFamily: FONT_DISPLAY }}>01</div>
                <div className="text-[10px] tracking-[0.18em] text-[#A7956E] font-bold mt-1 text-right">من 7</div>
              </div>
            </div>

            <div className="p-6 sm:p-7 space-y-6">
              <div>
                <div className="text-[10px] tracking-[0.24em] text-[#C9A66A] font-bold mb-2">الهدف من هذه الليلة</div>
                <div className="text-[14.5px] text-[#E7DDC0] leading-[1.95]">إزالة ثقل الروتين والرجوع لخفّة البدايات.</div>
              </div>

              <div className="bg-[#0A0606]/70 border border-[#C9A66A]/22 rounded-xl px-5 py-4">
                <div className="text-[10px] tracking-[0.24em] text-[#A7956E] font-bold mb-2">عبارة البداية</div>
                <div className="text-[15px] text-[#F5EAD3] leading-[1.95] italic" style={{ fontFamily: FONT_DISPLAY }}>
                  «خلينا الليلة ما نناقش مسؤوليات. أبغى نضحك شوي ونرجع خفاف.»
                </div>
              </div>

              <div>
                <div className="text-[10px] tracking-[0.24em] text-[#C9A66A] font-bold mb-3">5 أسئلة بسيطة</div>
                <ol className="space-y-2.5">
                  {[
                    "ما أغرب موقف صار بيننا وتضحك عليه الآن؟",
                    "ما عادة فيني تضحكك سرًا؟",
                    "لو نسافر يومين فقط، أين تود نروح؟",
                    "ما أكلة أو مكان يذكرك ببدايتنا؟",
                    "ما الشيء الذي كان بيننا زمان وتفتقده؟",
                  ].map((q, i) => (
                    <li key={i} className="flex gap-3 items-start text-[14px] text-[#E7DDC0] leading-[1.85]">
                      <span className="w-6 h-6 rounded-full bg-[#C9A66A]/15 border border-[#C9A66A]/35 text-[#C9A66A] font-extrabold text-[11px] tabular-nums flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-[#0A0606]/60 border border-[#C9A66A]/18 rounded-xl px-5 py-4">
                  <div className="text-[10px] tracking-[0.24em] text-[#C9A66A] font-bold mb-1.5">النشاط</div>
                  <div className="text-[13.5px] text-[#E7DDC0] leading-[1.85]">كل شخص يختار ذكرى مضحكة ويعيد حكايتها.</div>
                </div>
                <div className="bg-[#0A0606]/60 border border-[#C9A66A]/18 rounded-xl px-5 py-4">
                  <div className="text-[10px] tracking-[0.24em] text-[#C9A66A] font-bold mb-1.5">تحدّي لطيف</div>
                  <div className="text-[13.5px] text-[#E7DDC0] leading-[1.85]">خذي صورة عادية لكما في البيت كأنها ذكرى سفر.</div>
                </div>
              </div>

              <div className="pt-3 border-t border-[#C9A66A]/15 flex items-center gap-2 text-[11.5px] text-[#A7956E]">
                <Lock className="w-3.5 h-3.5 text-[#C9A66A]" />
                هذه ليلة واحدة من 14 ليلة كاملة + بطاقات + رسائل + أفكار ليالي بيتية في النسخة الكاملة.
              </div>
            </div>
          </div>

          <div className="mt-7 text-center">
            <button
              onClick={() => goCheckout("complete", "example-night")}
              data-testid="seven-example-cta"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606] font-extrabold text-[14px] tracking-wide rounded-full px-7 py-3.5 min-h-[52px] transition-all shadow-[0_12px_36px_-8px_rgba(201,166,106,0.55)]"
            >
              احصلي على كل الليالي الآن <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="mt-3 text-[11.5px] text-[#A7956E]">المحتوى يفتح فورًا بعد الدفع — بدون انتظار، بدون بريد.</div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — moved here to provide social proof right before pricing. */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04] relative overflow-hidden" data-testid="seven-testimonials">
        <div
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(201,166,106,0.22) 0%, transparent 70%)" }}
        />
        <div className="relative max-w-5xl mx-auto px-5">
          <SectionTitle kicker="آراء زوجات جربن البرنامج">قصص حقيقية من زوجات مثلكِ.</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="relative bg-gradient-to-b from-[#1C1010] to-[#150C0C] border border-[#C9A66A]/22 rounded-2xl p-6 sm:p-7"
                data-testid={`seven-testimonial-${i}`}
              >
                <Quote className="absolute top-5 left-5 w-7 h-7 text-[#C9A66A]/15" />
                <div className="flex items-center gap-1 mb-3.5">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 text-[#E2C281]" fill="#E2C281" />
                  ))}
                </div>
                <p className="text-[14px] text-[#E7DDC0] leading-[1.95] mb-5">«{t.text}»</p>
                <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#C9A66A]/12">
                  <div>
                    <div className="text-[13.5px] font-bold text-white">{t.name}</div>
                    <div className="text-[11.5px] text-[#A7956E]">{t.location}</div>
                  </div>
                  <div className="text-[10.5px] text-[#C9A66A] tracking-[0.18em] font-bold bg-[#C9A66A]/10 border border-[#C9A66A]/25 rounded-full px-2.5 py-1">
                    {t.bundle}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[11.5px] text-[#7A6F50] max-w-xl mx-auto leading-relaxed">
            * أسماء مستعارة لحماية خصوصية الزوجات — جميع المراجعات حقيقية ومن مشتركات سابقات.
          </p>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-5">
          <SectionTitle kicker="اختاري الباقة">ابدئي الليلة الأولى.</SectionTitle>
          <div className="grid md:grid-cols-3 gap-5 mt-2">
            {BUNDLES.map((b) => {
              const isMain = b.highlight;
              return (
                <div
                  key={b.key}
                  data-testid={`seven-bundle-${b.key}`}
                  className={`relative rounded-2xl p-7 flex flex-col transition-all ${
                    isMain
                      ? "bg-gradient-to-b from-[#2A1414] to-[#150C0C] border-2 border-[#C9A66A] shadow-[0_30px_80px_-25px_rgba(201,166,106,0.55)] md:scale-[1.04]"
                      : "bg-[#1C1010]/70 border border-[#C9A66A]/22 hover:border-[#C9A66A]/45"
                  }`}
                >
                  {isMain && (
                    <div className="absolute -top-3 right-7 inline-flex items-center gap-1 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] text-[#0A0606] text-[10.5px] tracking-[0.22em] font-extrabold px-3 py-1 rounded-full">
                      الأفضل قيمة
                    </div>
                  )}
                  <div className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold mb-2">{b.tagline}</div>
                  <div className="text-[24px] font-extrabold text-white leading-tight" style={{ fontFamily: FONT_DISPLAY }}>{b.label}</div>
                  <div className="mt-5 flex items-baseline gap-2">
                    {b.price_original && (
                      <span className="text-[16px] text-[#7A6F50] line-through tabular-nums font-bold" data-testid={`seven-bundle-price-original-${b.key}`}>{b.price_original}</span>
                    )}
                    <span className="text-[44px] font-black text-white tabular-nums leading-none">{b.price}</span>
                    <span className="text-[15px] font-bold text-[#C9A66A]">درهم</span>
                  </div>
                  {b.price_original && (
                    <div className="mt-1 inline-block self-start text-[10px] tracking-[0.18em] font-extrabold text-[#0A0606] px-2 py-0.5 rounded-full bg-gradient-to-b from-[#E2C281] to-[#C9A66A]" data-testid={`seven-bundle-save-badge-${b.key}`}>
                      وفّري {b.price_original - b.price} درهم
                    </div>
                  )}
                  <div className="text-[11.5px] text-[#A7956E] mt-1">دفعة واحدة · تسليم فوري</div>
                  <div className="my-5 h-px bg-[#C9A66A]/15" />
                  <ul className="space-y-2.5 text-[13.5px] text-[#E7DDC0] mb-6 flex-1">
                    {b.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5 leading-[1.7]">
                        <Check className="w-4 h-4 text-[#C9A66A] mt-0.5 shrink-0" strokeWidth={2.5} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => goCheckout(b.key, `pricing-${b.key}`)}
                    data-testid={`seven-bundle-cta-${b.key}`}
                    className={`w-full inline-flex items-center justify-center gap-2 font-extrabold text-[14px] tracking-wide rounded-full px-6 py-3.5 min-h-[52px] transition-all ${
                      isMain
                        ? "bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606] shadow-[0_10px_30px_-10px_rgba(201,166,106,0.65)]"
                        : "bg-transparent border border-[#C9A66A]/50 text-[#E7C98C] hover:bg-[#C9A66A]/10 hover:border-[#C9A66A]"
                    }`}
                  >
                    {isMain ? "ابدئي النسخة الكاملة" : "اختاري هذه النسخة"}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Bundle Comparison Snapshot — simplified to the 5 most decisive rows. */}
          <div className="mt-12 max-w-3xl mx-auto" data-testid="seven-bundle-comparison">
            <div className="text-center text-[10.5px] tracking-[0.32em] text-[#A7956E] font-bold mb-4">قارني بسرعة</div>
            <div className="overflow-hidden rounded-2xl border border-[#C9A66A]/22 bg-[#150C0C]/60 backdrop-blur-sm">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-[#C9A66A]/15">
                    <th className="text-right p-4 font-bold text-[#CFC3A1] w-[40%]">ما ستحصلين عليه</th>
                    <th className="text-center p-4 font-bold text-[#CFC3A1] text-[12px]">
                      <div>البداية</div>
                      <div className="text-[10px] text-[#A7956E] mt-0.5 font-normal" dir="ltr">99 AED</div>
                    </th>
                    <th className="text-center p-4 font-bold text-[#CFC3A1] text-[12px]">
                      <div>الرجوع</div>
                      <div className="text-[10px] text-[#A7956E] mt-0.5 font-normal" dir="ltr">149 AED</div>
                    </th>
                    <th className="text-center p-4 font-bold text-[#E7C98C] text-[12px] bg-[#C9A66A]/[0.08]">
                      <div>الكاملة ★</div>
                      <div className="text-[10px] text-[#C9A66A] mt-0.5 font-normal" dir="ltr">199 AED</div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[#E7DDC0]">
                  {[
                    { label: "برنامج 7 ليالٍ للعرسان", keys: ["yes", "yes", "yes"] },
                    { label: "برنامج 7 ليالٍ بعد الروتين", keys: [null, "yes", "yes"] },
                    { label: "البطاقات الرقمية", keys: ["70 بطاقة", "100 بطاقة", "150 بطاقة"] },
                    { label: "رسائل واتساب جاهزة + أفكار ليالي", keys: ["yes", "yes", "yes"] },
                    { label: "30 رسالة دلع + خطة شهرية للاستمرار", keys: [null, null, "yes"] },
                  ].map((row, i) => {
                    const renderCell = (val, isHighlight) => {
                      const cellBg = isHighlight ? "bg-[#C9A66A]/[0.08]" : "";
                      if (val === null) return <td className={`p-3.5 text-center ${cellBg}`}><X className="w-4 h-4 text-[#5A5340] mx-auto" /></td>;
                      if (val === "yes") return <td className={`p-3.5 text-center ${cellBg}`}><Check className={`w-4 h-4 mx-auto ${isHighlight ? "text-[#E2C281]" : "text-[#C9A66A]"}`} strokeWidth={2.5} /></td>;
                      return <td className={`p-3.5 text-center text-[12px] font-bold ${isHighlight ? "text-[#E7C98C]" : "text-[#F5EAD3]"} ${cellBg}`}>{val}</td>;
                    };
                    return (
                      <tr key={i} className="border-b border-white/[0.04] last:border-b-0 hover:bg-[#1C1010]/40 transition-colors">
                        <td className="p-3.5 text-right text-[13px] font-medium text-[#F5EAD3]">{row.label}</td>
                        {renderCell(row.keys[0], false)}
                        {renderCell(row.keys[1], false)}
                        {renderCell(row.keys[2], true)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-center text-[12px] text-[#A7956E]">
              النسخة الكاملة توفّر <span className="text-[#C9A66A] font-bold">ضعف القيمة</span> مقابل <span className="text-white">100 درهم إضافي</span> فقط.
            </p>
          </div>

          {/* Money-back guarantee + trust badges. Sits directly under pricing
              so the visitor sees risk-reversal before she ever leaves the
              section. */}
          <div className="mt-10 max-w-3xl mx-auto" data-testid="seven-guarantee-block">
            <div className="bg-gradient-to-b from-[#1C1010] to-[#150C0C] border border-[#C9A66A]/35 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-center gap-5 shadow-[0_20px_60px_-25px_rgba(201,166,106,0.4)]">
              <div className="w-16 h-16 rounded-full bg-[#C9A66A]/12 border border-[#C9A66A]/35 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-8 h-8 text-[#C9A66A]" strokeWidth={2} />
              </div>
              <div className="flex-1 text-center sm:text-right">
                <div className="text-[10.5px] tracking-[0.28em] text-[#C9A66A] font-bold mb-1.5">ضمان ٧ أيام</div>
                <h3 className="font-extrabold text-[18px] sm:text-[20px] text-white leading-tight mb-1.5" style={{ fontFamily: FONT_DISPLAY }}>
                  ما عجبكِ؟ نرجّع لكِ مبلغكِ كاملًا.
                </h3>
                <p className="text-[13.5px] text-[#CFC3A1] leading-[1.85]">
                  جربي البرنامج لمدة ٧ أيام بدون مخاطرة. لو ما حسّيتِ بفرق، راسلينا ونرجّع لكِ المبلغ كاملًا بدون أسئلة محرجة.
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5" data-testid="seven-trust-badges">
              <div className="inline-flex items-center gap-1.5 text-[11.5px] text-[#CFC3A1] bg-[#1C1010]/70 border border-[#C9A66A]/20 rounded-full px-3 py-1.5">
                <Lock className="w-3 h-3 text-[#C9A66A]" />
                دفع آمن عبر Stripe
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11.5px] text-[#CFC3A1] bg-[#1C1010]/70 border border-[#C9A66A]/20 rounded-full px-3 py-1.5">
                <CreditCard className="w-3 h-3 text-[#C9A66A]" />
                Apple Pay · Visa · Mastercard
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11.5px] text-[#CFC3A1] bg-[#1C1010]/70 border border-[#C9A66A]/20 rounded-full px-3 py-1.5">
                <ShieldCheck className="w-3 h-3 text-[#C9A66A]" />
                تشفير SSL · خصوصية كاملة
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11.5px] text-[#CFC3A1] bg-[#1C1010]/70 border border-[#C9A66A]/20 rounded-full px-3 py-1.5">
                <Check className="w-3 h-3 text-[#C9A66A]" strokeWidth={3} />
                وصول فوري بعد الدفع
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24 border-b border-white/[0.04]">
        <div className="max-w-2xl mx-auto px-5">
          <SectionTitle kicker="أسئلة شائعة">أسئلة قد تخطر ببالك.</SectionTitle>
          <div className="space-y-2.5">
            {FAQ_DATA.map((f, i) => (
              <button
                key={i}
                onClick={() => { setFaqOpen(faqOpen === i ? null : i); if (faqOpen !== i) track("faq_open", { i }); }}
                data-testid={`seven-faq-${i}`}
                className="w-full text-right bg-[#1C1010]/60 border border-[#C9A66A]/15 hover:border-[#C9A66A]/40 rounded-xl px-5 py-4 transition-all"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[14.5px] text-white font-bold">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#C9A66A] transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
                </div>
                {faqOpen === i && (
                  <div className="mt-3 text-[13.5px] text-[#CFC3A1] leading-[1.95]">{f.a}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-bold text-[28px] sm:text-[38px] leading-[1.3] text-white" style={{ fontFamily: FONT_DISPLAY, fontWeight: 800 }}>
            لا تنتظري مناسبة كبيرة.
            <br/>
            <span className="text-[#C9A66A]">أحيانًا العلاقة تحتاج ليلة واحدة تبدأ منها.</span>
          </h2>
          <button
            onClick={() => goCheckout("complete", "final")}
            data-testid="seven-final-cta"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606] font-extrabold text-[15px] tracking-wide rounded-full px-9 py-4 min-h-[58px] transition-all shadow-[0_14px_44px_-8px_rgba(201,166,106,0.55)]"
          >
            ابدئي الليلة الأولى <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="mt-4 text-[11.5px] text-[#A7956E] flex items-center justify-center gap-2">
            <Lock className="w-3 h-3" /> دفع آمن عبر Stripe · ضمان خصوصية كاملة
          </div>
        </div>
      </section>

      <footer className="py-9 border-t border-white/[0.05] text-center px-5">
        <p className="text-[11.5px] text-[#7A6F50] max-w-lg mx-auto leading-relaxed">
          7 ليالي بيننا — منتج رقمي مستقل خاص بالأزواج. ليس استشارة زوجية أو علاج نفسي.
        </p>
      </footer>

      {/* Floating WhatsApp-styled conversion chatbot */}
      <SevenChatbot />

      {/* MOBILE STICKY CTA — mirrors the Reply Rescue pattern. Keeps the
          primary call-to-action permanently visible while scrolling on
          phones (where the hero CTA scrolls off-screen). Defaults to the
          complete bundle (highest conversion). */}
      <div
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-[#1A0F0F]/95 backdrop-blur border-t border-[#C9A66A]/15 p-3 flex items-center gap-3"
        data-testid="seven-sticky-cta-bar"
      >
        <div className="text-[11px] text-[#A7956E] leading-tight flex-shrink-0">
          <div className="font-bold text-[#F5EAD3]">7 ليالي بيننا</div>
          <div className="tabular-nums" dir="ltr">199 AED · النسخة الكاملة</div>
        </div>
        <button
          onClick={() => goCheckout("complete", "sticky_cta")}
          data-testid="seven-sticky-cta"
          className="ml-auto bg-gradient-to-r from-[#E2C281] to-[#C9A66A] text-[#0A0606] font-extrabold text-sm px-5 py-3 rounded-full min-h-[48px] flex items-center gap-1.5 shadow-[0_8px_24px_-8px_rgba(201,166,106,0.6)] active:scale-[0.97] transition-transform"
        >
          ابدئي الآن <ArrowLeft className="w-4 h-4" />
        </button>
      </div>
      {/* Spacer so the sticky CTA doesn't cover the footer on phones. */}
      <div className="sm:hidden h-20" aria-hidden />
    </div>
  );
}
